import React from 'react';
import { Home, Plane, Building2, MessageSquare, Bookmark } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { fireHaptic } from '../hooks/useHaptics';

/**
 * MobileBottomNav — Sprint E · V4 Ultra-Luxury bottom navigation.
 *
 * A fixed, safe-area-aware bottom bar for phones. Hidden on ≥ md screens
 * (desktop keeps the top Header). Five tabs mirror the desktop nav:
 *   Home · Flights · Hotels · Chat · Saved
 *
 * Aesthetic (V4):
 *   · Off-White backdrop with a whisper-thin gold hairline on top.
 *   · Active tab is highlighted with a Midnight-Blue icon + a Soft-Gold
 *     dot indicator below the label. No pill fills. No shadows.
 *   · Labels are tiny Inter uppercase tracking-[0.28em] — quiet by design.
 *
 * Haptics:
 *   · Every tap fires `navigator.vibrate(10)` — a barely-there tick that
 *     mirrors iOS taptic UX on Android. Gracefully no-ops on iOS Safari
 *     (which never exposed the API), which is fine — iOS handles haptics
 *     via CSS `-webkit-tap-highlight-color` and the OS itself.
 */
const NAV_ITEMS = [
  { key: 'home',    icon: Home,          labelKey: 'nav.home',    fallback: 'Home' },
  { key: 'flights', icon: Plane,         labelKey: 'nav.flights', fallback: 'Flights' },
  { key: 'hotels',  icon: Building2,     labelKey: 'nav.hotels',  fallback: 'Hotels' },
  { key: 'chat',    icon: MessageSquare, labelKey: 'nav.chat',    fallback: 'Chat' },
  { key: 'saved',   icon: Bookmark,      labelKey: 'nav.saved',   fallback: 'Saved' },
];

// Some routes/sections use aliases (e.g. the home experience is the empty
// pathname). This map normalises `activeSection` → the tab that should
// glow. Anything not in the map matches the tab whose `key` equals
// `activeSection` (identity default).
const ACTIVE_ALIAS = {
  '':          'home',
  'chat-home': 'chat',
  'ai':        'chat',
  'ai-chat':   'chat',
  'packages':  'saved',
};

export default function MobileBottomNav({ activeSection, onNavigate }) {
  const { t } = useLanguage();
  const normalised = ACTIVE_ALIAS[activeSection] ?? activeSection;

  const handleTap = (key) => {
    fireHaptic('nudge');
    if (onNavigate) onNavigate(key);
  };

  return (
    <nav
      role="navigation"
      aria-label="Primary mobile navigation"
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ivory-50/95 backdrop-blur border-t border-gold-300/50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Whisper-thin gold hairline flourish at the top edge. */}
      <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-400/60 to-transparent" />
      <ul className="grid grid-cols-5">
        {NAV_ITEMS.map(({ key, icon: Icon, labelKey, fallback }) => {
          const isActive = normalised === key;
          return (
            <li key={key} className="flex">
              <button
                type="button"
                onClick={() => handleTap(key)}
                data-testid={`bottom-nav-${key}`}
                aria-current={isActive ? 'page' : undefined}
                className={`relative flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[54px] focus:outline-none focus-visible:bg-gold-50 transition-colors active:bg-gold-100/60 ${
                  isActive ? 'text-midnight-900' : 'text-midnight-500 hover:text-midnight-800'
                }`}
              >
                <Icon
                  className={`w-[18px] h-[18px] transition-transform ${isActive ? 'scale-110' : ''}`}
                  strokeWidth={isActive ? 2.2 : 1.6}
                />
                <span className="text-[9px] uppercase tracking-[0.28em] font-semibold leading-none">
                  {t(labelKey) || fallback}
                </span>
                {/* Active indicator — a single soft-gold dot below the label. */}
                <span
                  aria-hidden
                  className={`absolute bottom-1 w-1 h-1 rounded-full bg-gold-500 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
